import Box from '@/common/components/Box'
import Button from '@/common/components/Button'
import Checkbox from '@/common/components/Checkbox'
import { ErrorMessageWrapper } from '@/common/components/ErrorMessageWrapper'
import GoBackButton from '@/common/components/GoBackButton'
import TextField from '@/common/components/TextField'
import useDebounce from '@/common/hooks/useDebounce'
import useRole from '@/features/tcardRoles/hooks/useRole'
import { BadgeHelpIcon, ChevronsRightIcon, SearchIcon } from 'lucide-react'
import { Link, useParams } from 'react-router-dom'
import { Tooltip } from 'react-tooltip'

const Show = () => {
  const { id } = useParams()
  const [search, setSearch] = useDebounce('')

  const {
    data: role,
    isPending: roleIsPending,
    error: roleError = {} as any,
    refetch: refetchRole,
  } = useRole({ id: id || '' })

  const disableUpdateButton = roleIsPending || roleError?.status === 403

  return (
    <>
      <div className="flex justify-between items-center mb-6">
        <div className="flex gap-2 items-center">
          <GoBackButton fallback="/tcard-roles" searchLastRouteFromFallback />
          <p className="text-white-400 font-normal">Cargos e permissões - Tcard</p>
          <ChevronsRightIcon color="#ffffffb3" />
          {!roleIsPending && <h2 className="font-bold">{role?.display_name}</h2>}
        </div>
        <Link
          to={disableUpdateButton ? '' : `/tcard-roles/${id}/edit`}
          className={disableUpdateButton ? 'cursor-default' : ''}
        >
          <Button
            name="edit_roles"
            type="button"
            className="shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)]"
            disabled={disableUpdateButton}
          >
            Editar
          </Button>
        </Link>
      </div>
      {roleIsPending ? (
        <Box>Carregando...</Box>
      ) : (
        <ErrorMessageWrapper
          className="max-h-[calc(100vh-128px)]"
          statusCode={roleError?.status}
          refetchFunction={refetchRole}
        >
          <Box className="flex flex-col gap-y-6 shadow-[4px_4px_8px_4px_rgba(0,_0,_0,_0.2)] max-h-[calc(100vh-128px)] overflow-y-auto">
            <div className="flex flex-col gap-y-4">
              <p className="text-lg font-bold">Cargo</p>

              <TextField name="display_name" label="Nome do cargo" value={role?.display_name} disabled />

              <TextField name="description" label="Descrição do cargo" value={role?.description} disabled />

              <TextField name="level" label="Nível" value={role?.level?.toString()} disabled />

              <Checkbox
                name="can_bypass_linked_client_company_restriction"
                label="Permitir visualizar dados de empresas que não está vinculado como Responsável Interno ou Substituto do Responsável Interno"
                className="items-center gap-x-2"
                labelClassName="text-[0.625rem] font-light italic"
                checked={role?.can_bypass_linked_client_company_restriction}
                disabled
              />

              <Checkbox
                name="can_bypass_linked_establishment_company_restriction"
                label="Permitir visualizar dados de estabelecimentos que não está vinculado como Responsável Interno ou Substituto do Responsável Interno"
                className="items-center gap-x-2"
                labelClassName="text-[0.625rem] font-light italic"
                checked={role?.can_bypass_linked_establishment_company_restriction}
                disabled
              />
            </div>

            <Box className="flex flex-col gap-y-6">
              <div className="flex flex-col gap-y-4">
                <p>Permissões</p>

                <TextField
                  LeftIcon={<SearchIcon />}
                  placeholder="Pesquisar permissões"
                  onChange={(e) => setSearch((e.target as HTMLInputElement).value)}
                />
              </div>

              <div
                className="grid grid-cols-1 lg:grid-cols-2 gap-4 mt-2 overflow-auto min-h-14 max-h-[calc(100vh-566px)]"
                data-testid="permission_show_list"
              >
                {role?.permission_roles
                  ?.filter((permissionRole) =>
                    permissionRole.permission.display_name.toLowerCase().includes(search.toLowerCase()),
                  )
                  ?.map((permissionRole) => (
                    <div key={permissionRole.id} className="flex gap-2 items-center">
                      <Checkbox
                        name={permissionRole.permission.display_name}
                        label={permissionRole.permission.display_name}
                        labelClassName="max-w-[360px] text-wrap break-words cursor-default"
                        checked
                        disabled
                      />
                      <div data-tooltip-id={permissionRole.id} className="flex gap-1 items-center text-warning">
                        <BadgeHelpIcon strokeWidth={1} size={14} />
                        <p className="font-light text-[8px] italic">Informações</p>
                      </div>
                      <Tooltip id={permissionRole.id}>{permissionRole.permission.description}</Tooltip>
                    </div>
                  ))}
              </div>
            </Box>
          </Box>
        </ErrorMessageWrapper>
      )}
    </>
  )
}

export default Show
